import { Ban, Shield, ShieldOff, UserMinus } from "lucide-react";
import { useMemo } from "react";

import { useTranslations } from "@/shared/lang/useTranslations";

import { ACCESS_FILTER_ALL } from "../constants/access.constants";
import type { RolesBucket, RolesBucketCounts } from "../hooks/useRolesCatalog";
import { AccessKpiStrip, type AccessKpiTile } from "./AccessKpiStrip";

export interface RolesKpiStripProps {
  /** `null` while the catalog loads — every tile then reads `—` instead of asserting four zeros. */
  counts: RolesBucketCounts | null;
  value: RolesBucket;
  onChange: (next: RolesBucket) => void;
}

/**
 * Fixed order, and every tile after the first reports an ANOMALY rather than a category. Counting roles by
 * scope would only restate the badge already on every row; what a reader cannot see by scrolling is which
 * roles take access away, which grant nothing, and which nobody holds.
 *
 * The figures describe the set already narrowed by search and scope, so typing changes every tile — each
 * still reporting the size of its own bucket rather than of the current page.
 */
export function RolesKpiStrip({ counts, value, onChange }: RolesKpiStripProps) {
  const { t } = useTranslations("settings");

  const tiles = useMemo<AccessKpiTile<RolesBucket>[]>(
    () => [
      {
        key: ACCESS_FILTER_ALL,
        label: t("access_roles_kpi_total"),
        value: counts?.all ?? null,
        icon: Shield,
        accent: "neutral",
      },
      {
        key: "deny",
        label: t("access_roles_kpi_deny"),
        value: counts?.deny ?? null,
        // The meta only appears when there is something to explain; on zero it would be a caption for nothing.
        meta: counts && counts.deny > 0 ? t("access_roles_kpi_deny_meta") : undefined,
        icon: Ban,
        accent: "warning",
      },
      {
        key: "no_policies",
        label: t("access_roles_kpi_no_policies"),
        value: counts?.no_policies ?? null,
        meta: counts && counts.no_policies > 0 ? t("access_roles_kpi_no_policies_meta") : undefined,
        icon: ShieldOff,
        accent: "danger",
      },
      {
        key: "no_members",
        label: t("access_roles_kpi_no_members"),
        value: counts?.no_members ?? null,
        icon: UserMinus,
        accent: "neutral",
      },
    ],
    [t, counts],
  );

  return (
    <AccessKpiStrip
      ariaLabel={t("access_roles_kpi_group_label")}
      tiles={tiles}
      value={value}
      onChange={onChange}
    />
  );
}
